/* global google */
import React, { useState, useCallback, useRef, useMemo, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { debounce } from 'lodash';

import { centerOnBoundingBox } from '../../../utilities/map-utilities';

import Icon from '@material-ui/core/Icon';
import Select, { components } from 'react-select';

import LocationButton from '../../map/location-button';

import {
  getSelection,
  getSuggestions,
  getSearchTerm,
  getSearchFocused,
  getLinkedElement
} from '../../../selectors/search';
import {
  getInitialUrlFilter
} from '../../../selectors/filters';

import {
  fetchGeocodeSuggestions,
  setSearchTerm,
  setSearchSelection,
  clearSearchSuggestions,
  setSearchFocused,
  clearSearchSelection
} from '../../../actions/search-actions';
import { clearInitialUrlFilter } from '../../../actions/filter-actions';

import styles from './map-search-autocomplete.scss';

const ident = value => value;


const MagnifierControl = ({children, ...rest }) => {
  return (
    <components.Control {...rest}>
      <div className={styles.searchIcon}><Icon translate="no">search</Icon></div>
      {children}
    </components.Control>
  );
};
MagnifierControl.propTypes = {
  children: PropTypes.node
};


const LocationDropDown = () => {
  return <div className={styles.locationPad} />;
};

const ClearIcon = ({ innerProps }) => {
  return (
    <div
      {...innerProps}
      className={styles.clearIcon}
    >
      <Icon translate="no">cancel</Icon>
    </div>
  );
};
ClearIcon.propTypes = {
  innerProps: PropTypes.object
};

const ClearWardsIcon = ({ innerProps }) => {
  return (
    <div
      {...innerProps}
      className={styles.clearIcon}
    >
      <div>CLEAR</div>
    </div>
  );
};
ClearWardsIcon.propTypes = {
  innerProps: PropTypes.object
};

const NoOptions = () => (<span>No results found</span>);
const OptionLabel = ({label, props}) => (<span {...props}>{label}</span>);
OptionLabel.propTypes = {
  label: PropTypes.string,
  props: PropTypes.object
};

const MapSearchAutocomplete = ({
  landing,
  mapRef,
  mobile,
  showClearIcon,
  showMagnifier,
  showMyLocation
}) => {
  const dispatch = useDispatch();
  const [input, setInput] = useState(null);

  const linkedElement = useSelector(getLinkedElement);
  const searchTerm = useSelector(getSearchTerm);
  const selection = useSelector(getSelection);
  const suggestions = useSelector(getSuggestions);
  const focused = useSelector(getSearchFocused);
  const initialUrlFilter = useSelector(getInitialUrlFilter);

  const selectionRef = useRef(selection);
  const autoCompleteSessionRef = useRef(null);
  const linkedElementRef = useRef(null);

  const loading = Boolean(searchTerm && suggestions === null);
  const menu = input && !loading && suggestions && !mobile;
  const inputValue = (input !== null ? input : selection && selection.label || '');

  const options = useMemo(() => suggestions || [], [suggestions]);

  const changeValues = useCallback((selected, { action }) => {
    setInput(null);
    if ((action === 'clear' || action === 'select-option') && initialUrlFilter) {
      dispatch(clearInitialUrlFilter());
    }
    if (selected) {
      const {length = null, bbox} = selected;
      if (length !== null) {
        return;
      }
      dispatch(setSearchSelection(selected));
      if (bbox && mapRef) {
        centerOnBoundingBox(bbox, mapRef);
      }
    } else {
      dispatch(clearSearchSelection());
    }
  }, [mapRef, dispatch, setInput, initialUrlFilter]);

  const getOptions = useCallback((value) => {
    if (!value) {
      return;
    }
    if (!autoCompleteSessionRef.current) {
      autoCompleteSessionRef.current = new google.maps.places.AutocompleteSessionToken();
    }
    dispatch(fetchGeocodeSuggestions(value, mapRef, autoCompleteSessionRef));
    dispatch(setSearchTerm(value));
  }, [mapRef, dispatch]);
  const getOptionsDebounced = useMemo(() => {
    return debounce(getOptions, 500);
  }, [getOptions]);

  const inputChange = useCallback((value, { action }) => {
    if (action === 'input-change') {
      setInput(value);
      dispatch(clearSearchSuggestions());
      getOptionsDebounced(value);
      if (!value) {
        dispatch(clearSearchSelection());
      }
    }
    if (action === 'input-blur') {
      setInput(null);
    }
    return null;
  }, [dispatch, setInput, getOptionsDebounced]);

  const onClose = useCallback(() => {
    if (!mobile) {
      dispatch(clearSearchSuggestions());
      dispatch(setSearchFocused(false));
    }
  }, [mobile, dispatch]);

  const onFocus = useCallback(
    () => (dispatch(setSearchFocused(true)))
    , [dispatch]
  );
  const onBlur = useCallback(
    () => (!mobile && dispatch(setSearchFocused(false)))
    , [mobile, dispatch]
  );

  const selectComponents = useMemo(() => ({
    Control: showMagnifier ? MagnifierControl : components.Control,
    DropdownIndicator: showMyLocation ? LocationDropDown : null,
    IndicatorSeparator: null,
    ClearIndicator: (showClearIcon && (selection?.type === 'Wards' ? ClearWardsIcon : ClearIcon)) || null
  }), [showClearIcon, showMagnifier, showMyLocation, selection]);

  if (linkedElementRef.current !== linkedElement) {
    this.changeValues(linkedElement, '');
    linkedElementRef.current = linkedElement;
  }
  if (selectionRef.current !== selection) {
    if (selection) {
      autoCompleteSessionRef.current = null;
    }
    selectionRef.current = selection;
  }

  return (
    <div className={
      classNames(
        styles.mapSearchAutocompleteContainer,
        {
          [styles.landingSearchContainer]: landing,
          [styles.mobileSearchInput]: mobile,
          [styles.hasMenu]: menu
        }
      )
    }>
      <Select
        autoFocus={mobile || focused} // eslint-disable-line jsx-a11y/no-autofocus
        backspaceRemovesValue={false}
        blurInputOnSelect
        classNamePrefix={styles.mapSearchPrefix}
        components={selectComponents}
        placeholder={<div>Address, Neighborhood, ZIP code,<span translate="no">&nbsp;Ward</span></div>}
        noOptionsMessage={NoOptions}
        isLoading={loading}
        isClearable={selection}
        menuIsOpen={menu}
        options={options}
        formatOptionLabel={OptionLabel}
        filterOption={ident}
        value={selection}
        onChange={changeValues}
        inputValue={inputValue}
        onInputChange={inputChange}
        onMenuClose={onClose}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {showMyLocation &&
        <div className={styles.locationOverlay}>
          <LocationButton search />
        </div>
      }
    </div>
  );
};

MapSearchAutocomplete.propTypes = {
  landing: PropTypes.bool,
  mapRef: PropTypes.object,
  mobile: PropTypes.bool,
  showClearIcon: PropTypes.bool,
  showMagnifier: PropTypes.bool,
  showMyLocation: PropTypes.bool
};

export default memo(MapSearchAutocomplete);

